/**
 * 订阅编辑弹窗。
 *
 * 用于在订阅中心/个股详情编辑单个订阅的：
 * - max_per_push：单次推送上限（滑块 1-30，默认 10）
 * - dimensions：推送内容维度
 * - minImpact：最小影响度
 * - pushplusToken：该股票专属推送 token（可空，空则用全局 token）
 */
import { useEffect, useState } from "react";
import { X, Save, Loader2 } from "lucide-react";
import type { Subscription } from "@/services/api";
import { cn } from "@/lib/utils";

const DIMS: { key: keyof Subscription["dimensions"]; label: string; desc: string }[] = [
  { key: "quote", label: "行情异动", desc: "价格 / 成交量 / 资金流向" },
  { key: "sector", label: "板块动态", desc: "所属板块指数与成分股" },
  { key: "news", label: "关联新闻", desc: "国内财经媒体关联报道" },
  { key: "announce", label: "公司公告", desc: "交易所披露的公司公告" },
  { key: "international", label: "国际影响", desc: "Finnhub 国外源 + 翻译" },
];

const IMPACTS = [
  { value: "high", label: "高", desc: "只推高影响" },
  { value: "medium", label: "中", desc: "中影响及以上" },
  { value: "low", label: "低", desc: "全部推送" },
];

export default function SubEditModal({
  sub,
  onClose,
  onSave,
}: {
  sub: Subscription;
  onClose: () => void;
  onSave: (patch: Partial<Subscription>) => Promise<void>;
}) {
  const [maxPerPush, setMaxPerPush] = useState(sub.maxPerPush || 10);
  const [dimensions, setDimensions] = useState({ ...sub.dimensions });
  const [minImpact, setMinImpact] = useState(sub.minImpact || "low");
  const [token, setToken] = useState(sub.pushplusToken || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // ESC 关闭
  useEffect(() => {
    const fn = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  const toggleDim = (k: keyof Subscription["dimensions"]) =>
    setDimensions((d) => ({ ...d, [k]: !d[k] }));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await onSave({
        maxPerPush,
        dimensions,
        minImpact,
        pushplusToken: token.trim(),
      });
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-lg max-h-[90vh] overflow-auto animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头 */}
        <div className="flex items-center justify-between p-5 border-b border-ink-700 sticky top-0 bg-ink-850 z-10">
          <div>
            <h2 className="font-serif text-lg text-ink-100">编辑订阅</h2>
            <div className="num text-xs text-ink-500 mt-0.5">
              {sub.stockName} · {sub.stockCode}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-ink-500 hover:text-ink-200 p-1"
            aria-label="关闭"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* max_per_push 滑块 */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-ink-200 font-medium">
                单次推送条数上限
              </label>
              <span className="num text-lg text-gold text-gold-glow">
                {maxPerPush}
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={30}
              value={maxPerPush}
              onChange={(e) => setMaxPerPush(Number(e.target.value))}
              className="w-full accent-gold cursor-pointer"
            />
            <div className="flex justify-between text-[0.625rem] text-ink-500 mt-1 num">
              <span>1</span>
              <span>10 默认</span>
              <span>30</span>
            </div>
            <p className="text-[0.6875rem] text-ink-500 mt-2">
              单只股票单次推送最多包含的新闻条数。合并推送时按股数自动分配总预算。
            </p>
          </section>

          {/* 维度 */}
          <section>
            <div className="text-[0.6875rem] font-mono text-ink-500 uppercase tracking-wider mb-2">
              推送内容维度
            </div>
            <div className="space-y-1.5">
              {DIMS.map((d) => {
                const on = dimensions[d.key];
                return (
                  <button
                    key={d.key}
                    onClick={() => toggleDim(d.key)}
                    className={cn(
                      "w-full flex items-center justify-between p-2.5 border text-left transition-colors",
                      on
                        ? "border-gold/40 bg-gold/5"
                        : "border-ink-700 hover:border-ink-600",
                    )}
                  >
                    <div>
                      <div className={cn("text-sm", on ? "text-gold" : "text-ink-300")}>
                        {d.label}
                      </div>
                      <div className="text-[0.6875rem] text-ink-500">{d.desc}</div>
                    </div>
                    <span
                      className={cn(
                        "w-4 h-4 border flex items-center justify-center shrink-0 transition-colors",
                        on ? "bg-gold border-gold" : "border-ink-600",
                      )}
                    >
                      {on && (
                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6l3 3 5-6" stroke="#0A0B0D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* 最小影响度 */}
          <section>
            <div className="text-[0.6875rem] font-mono text-ink-500 uppercase tracking-wider mb-2">
              最小影响度
            </div>
            <div className="grid grid-cols-3 gap-2">
              {IMPACTS.map((im) => (
                <button
                  key={im.value}
                  onClick={() => setMinImpact(im.value)}
                  className={cn(
                    "p-2.5 border text-center transition-colors",
                    minImpact === im.value
                      ? "border-gold bg-gold/10 text-gold"
                      : "border-ink-700 text-ink-300 hover:border-ink-600",
                  )}
                >
                  <div className="text-sm font-medium">{im.label}</div>
                  <div className="text-[0.625rem] text-ink-500 mt-0.5">{im.desc}</div>
                </button>
              ))}
            </div>
          </section>

          {/* 专属 pushplus token */}
          <section>
            <div className="text-[0.6875rem] font-mono text-ink-500 uppercase tracking-wider mb-2">
              专属 pushplus token（可选）
            </div>
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="留空则使用全局 token"
              className="w-full bg-ink-900 border border-ink-700 focus:border-gold/60 px-3 py-2 text-sm text-ink-100 num outline-none"
            />
            <p className="text-[0.6875rem] text-ink-500 mt-1.5">
              填写后该股票的推送会发到这个 token 对应的微信；留空则统一用后端全局 token。
            </p>
          </section>

          {err && (
            <div className="text-xs text-bear border border-bear/40 bg-bear/10 px-3 py-2">
              {err}
            </div>
          )}
        </div>

        {/* 底部 */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-ink-700 sticky bottom-0 bg-ink-850">
          <button onClick={onClose} className="btn-ghost text-sm">
            取消
          </button>
          <button onClick={save} disabled={saving} className="btn-gold text-sm">
            {saving ? (
              <><Loader2 size={14} className="animate-spin mr-1.5" />保存中</>
            ) : (
              <><Save size={14} className="mr-1.5" />保存</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
